import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ModalInTailwind from '$components/ModalInTailwind/ModalInTailwind';
import { GetSharedCandidatesRosterDataOutput } from '$store/modules/candidates';
import { ReactComponent as GoTo } from '$icons/ForwardArrow.svg';
import { ReactComponent as CloseModal } from '$icons/uncolored/Close.svg';
import DefaultIcon2 from '$components/DefaultIcon2/DefaultIcon2';
import SPKCandidateCard from './SPKCandidateCard/SPKCandidateCard';
import SPKPanel from './SPKPanel/SPKPanel';
import SPKVideoModal from './SPKVideoModal/SPKVideoModal';

const SPKCandidateList = ({
  candidates,
  shareCandidatesIds,
}: {
  candidates: GetSharedCandidatesRosterDataOutput['candidates'];
  shareCandidatesIds: string;
}) => {
  const [showCandidateSidebar, setShowCandidateSidebar] = useState(false);
  const [showLoginOrRegisterModal, setShowLoginOrRegisterModal] =
    useState(false);
  const [selectedCandidate, setSetSelectedCandidate] = useState<
    undefined | GetSharedCandidatesRosterDataOutput['candidates'][number]
  >();
  const [isOpenSmartScore, setIsOpenSmartScore] = useState(false);
  const [isOpenVideoModal, setIsOpenVideoModal] = useState(false);

  const navigate = useNavigate();
  const [t] = useTranslation('global');

  const modalContent = useRef<HTMLDivElement>(null);

  const handleCloseShowCandidate = () => {
    modalContent.current?.classList.remove('translate-x-0');
    modalContent.current?.classList.add('translate-x-full');
    setTimeout(() => {
      setSetSelectedCandidate(undefined);
      setShowCandidateSidebar(false);
    }, 500);
  };

  const showCandidateSidebarHandler = (
    candidateData: GetSharedCandidatesRosterDataOutput['candidates'][number],
    smartScoreIsOpen?: boolean
  ) => {
    setShowCandidateSidebar(true);
    setSetSelectedCandidate(candidateData);
    setIsOpenSmartScore(smartScoreIsOpen || false);
  };

  const handleShowVideoModal = (
    candidateData: GetSharedCandidatesRosterDataOutput['candidates'][number]
  ) => {
    setIsOpenVideoModal(true);
    setSetSelectedCandidate(candidateData);
  };

  const handleShowLoginOrRegisterModal = (
    candidateData: GetSharedCandidatesRosterDataOutput['candidates'][number]
  ) => {
    setSetSelectedCandidate(candidateData);
    setShowLoginOrRegisterModal(true);
  };

  useEffect(() => {
    if (showCandidateSidebar) {
      modalContent.current?.classList.remove('translate-x-full');
      modalContent.current?.classList.add('translate-x-0');
    }
  }, [showCandidateSidebar]);

  return (
    <>
      {/* Panel Modal */}
      <ModalInTailwind
        isOpen={showCandidateSidebar}
        closeModal={handleCloseShowCandidate}
        position="right"
      >
        <SPKPanel
          candidate={
            selectedCandidate as GetSharedCandidatesRosterDataOutput['candidates'][number]
          }
          parentRef={modalContent}
          onClose={handleCloseShowCandidate}
          smartScoreInitialStatus={isOpenSmartScore}
        />
      </ModalInTailwind>
      {/* Video Modal */}
      <ModalInTailwind
        isOpen={isOpenVideoModal}
        closeModal={() => setIsOpenVideoModal(false)}
      >
        <SPKVideoModal
          selectedCandidate={
            selectedCandidate as GetSharedCandidatesRosterDataOutput['candidates'][number]
          }
        />
      </ModalInTailwind>
      {/* Login or register modal */}
      <ModalInTailwind
        isOpen={showLoginOrRegisterModal}
        closeModal={() => setShowLoginOrRegisterModal(false)}
      >
        <div className="max-w-2xl w-full bg-white p-8 rounded-2xl relative">
          <div>
            <span className="font-bold">
              {t('sharedPosition.sharedCandidatesRoster.modalTitle')}
            </span>
            <button
              aria-label="close modal"
              type="button"
              onClick={() => {
                setShowLoginOrRegisterModal(false);
              }}
            >
              <CloseModal className="absolute right-0 top-0 mr-2 mt-2 stroke-spk-brightBlue" />
            </button>
          </div>
          <hr className="my-4" />
          <div className="flex align-middle items-center gap-6 font-semibold">
            <div className="flex align-middle items-center flex-1 w-full">
              <div className="w-12 mr-2">
                <DefaultIcon2
                  picture={selectedCandidate?.User?.ProfilePicture?.Url}
                  firstName={selectedCandidate?.User.FirstName}
                  lastName={selectedCandidate?.User?.LastName}
                />
              </div>
              <span className="flex-1">
                {selectedCandidate?.User.FirstName}{' '}
                {selectedCandidate?.User?.LastName}.
              </span>
            </div>
            <button
              type="button"
              className="text-spk-brightBlue"
              onClick={() => {
                navigate('/employer_login', {
                  state: { shareCandidatesIds },
                });
              }}
            >
              {t('sharedPosition.login')}
            </button>
            <button
              type="button"
              className="flex align-middle items-center gap-2 text-white bg-spk-brightBlue rounded p-3"
              onClick={() => {
                navigate('/employer_register', {
                  state: { shareCandidatesIds },
                });
              }}
            >
              {t('sharedPosition.createAccount')}{' '}
              <GoTo className="fill-white" />
            </button>
          </div>
        </div>
      </ModalInTailwind>
      <div className="gap-4 flex flex-col">
        {candidates.map((singleCandidate) => {
          return (
            <SPKCandidateCard
              key={`skills_candidate_${singleCandidate.Id}`}
              candidate={singleCandidate}
              showSidebarHandler={showCandidateSidebarHandler}
              showVideoModalHandler={handleShowVideoModal}
              showLoginOrRegisterModalHandler={handleShowLoginOrRegisterModal}
            />
          );
        })}
      </div>
    </>
  );
};

export default SPKCandidateList;
